/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, FormField, Input, Heading, Form } from '@livechat/design-system-react-components';
import { getCountryCode } from 'countries-list';
import IntlTelInput from 'intl-tel-input/react'; // eslint-disable-line import/no-unresolved
import 'intl-tel-input/styles'; // eslint-disable-line import/no-unresolved

import Config from 'Config';
import { PhoneType, SignupFormData, FormMessageType } from 'types/form';
import { Product } from 'types/product';
import { Layout } from 'components/layout/Layout';
import { CustomLink } from 'components/link/Link';
import { PasswordRequirements } from 'components/password-requirements/PasswordRequirements';
import { useBeforeUnload } from 'hooks/use-before-unload';
import { useTabTitleChange } from 'hooks/use-tab-title-change';
import { useGetCountry } from 'hooks/use-get-country';
import { LoginWithButton } from 'components/button/LoginWithButton';
import { FormMessage } from 'components/forms/FormMessage';
import { errorDeserialize } from 'helpers/error-deserializer';
import { isMobile } from 'helpers/device';
import { NestedIData } from 'stores/base-store';
import Validator from 'services/Validator';
import { PHONE_NUMBER_ERROR_MAP } from 'constants/phone-number';
import { submitPost } from 'helpers/form';
import { validateEmail } from 'helpers/validators/email';
import { validateName } from 'helpers/validators/name';
import { validatePassword } from 'helpers/validators/password';
import { detectProduct, FREE_PRODUCTS, getProductName } from 'helpers/product';
import { saveItem } from 'helpers/storage/local-storage';
import SignupTracking, { SignupAuthMethod } from 'services/SignupTracking';

import SignupLegalInfo from './SignupLegalInfo';

import * as styles from './styles';

declare const window: any;

type Props = {
  formData: SignupFormData;
  onFormDataChange(data: Partial<SignupFormData>): void;
  onSubmit: (e: any) => void;
  isSignupPhoneLimitException: boolean;
  trackingForAllProducts: () => NestedIData;
};

export const SignupInfo: React.FC<Props> = ({
  formData,
  onFormDataChange,
  onSubmit,
  isSignupPhoneLimitException,
  trackingForAllProducts,
}) => {
  const phoneRef = useRef(null);
  const detectedProduct = detectProduct();

  const shouldShowFullNameField =
    detectedProduct === Product.OpenWidget ||
    (!isMobile() &&
      ![Product.LiveChat, Product.TextApp, Product.Undefined].includes(detectedProduct));
  const shouldShowPhoneField = !FREE_PRODUCTS.includes(getProductName(detectedProduct));

  const [hasFormChanged, setHasFormChanged] = useState<boolean>(false);
  const [emailValidationError, setEmailValidationError] = useState('');
  const [passwordValidationError, setPasswordValidationError] = useState('');
  const [nameValidationError, setNameValidationError] = useState('');
  const [phoneErrorCode, setPhoneErrorCode] = useState<number | null>(null);
  const [isPhoneMobile, setIsPhoneMobile] = useState<boolean>(false);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean | null>(null);
  const [phoneValidationError, setPhoneValidationError] = useState('');
  const [isPhoneScriptLoading, setIsPhoneScriptLoading] = useState<boolean>(true);

  useBeforeUnload(hasFormChanged);
  const { changedPageTitle } = useTabTitleChange(['👋 Almost there…', 'Finish your account setup']);
  const { country, isLoading: isGetCountryLoading } = useGetCountry();

  useEffect(() => {
    (async () => {
      if (!isGetCountryLoading) {
        try {
          setIsPhoneScriptLoading(true);
          onFormDataChange({ country });
          const countryCode = getCountryCode(country);
          phoneRef?.current?.getInstance().setCountry(countryCode);
        } catch (e) {
          throw new Error(`Country couldn't be fetched. [${errorDeserialize(e)}]`);
        } finally {
          setIsPhoneScriptLoading(false);
        }
      }
    })();
  }, [isGetCountryLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailValidation = validateEmail(formData.email);
    const passwordValidation = validatePassword(formData.password);
    const nameInvalid = shouldShowFullNameField && validateName(formData.name);
    const phoneInvalid = shouldShowPhoneField && formData.phone && !isPhoneValid;

    if (emailValidation) {
      setEmailValidationError(emailValidation);
    }

    if (passwordValidation) {
      setPasswordValidationError(passwordValidation);
    }

    if (nameInvalid) {
      setNameValidationError(nameInvalid);
    }

    if (phoneInvalid) {
      setPhoneValidationError(
        PHONE_NUMBER_ERROR_MAP[phoneErrorCode] || 'Invalid phone number format'
      );
    } else if (formData.phone && !isPhoneMobile) {
      setPhoneValidationError('Enter a valid mobile phone number');
    }

    if (!emailValidation && !passwordValidation && !nameInvalid && !phoneInvalid) {
      setHasFormChanged(false);
    }

    if (
      (formData.phone && (!isPhoneValid || (isPhoneValid && !isPhoneMobile))) ||
      emailValidation ||
      passwordValidation ||
      nameInvalid
    ) {
      return;
    }

    if (formData.url) {
      onFormDataChange({ url: Validator.removeTrailingSlash(formData.url) });
    }

    onSubmit(e);
  };

  const getSignUpTracking = () => {
    const orgTracking = trackingForAllProducts();

    return JSON.stringify(orgTracking);
  };

  const handleFormDataChange = (data: Partial<SignupFormData>) => {
    if (data.name || data.email || data.password || data.phone) {
      setHasFormChanged(true);
    } else {
      setHasFormChanged(false);
    }

    onFormDataChange(data);
  };

  const handleEmailInputChange = (e) => {
    handleFormDataChange({ email: e.target.value });
    if (!validateEmail(e.target.value.trim())) {
      setEmailValidationError(null);
    }
  };

  const handlePasswordInputChange = (e) => {
    handleFormDataChange({ password: e.target.value });
    if (!validatePassword(e.target.value.trim())) {
      setPasswordValidationError(null);
    }
  };

  const handleNameInputChange = (e) => {
    handleFormDataChange({ name: e.target.value });
    if (e.target.value.trim() !== '') {
      setNameValidationError(null);
    }
  };

  const handlePhoneInputChange = (phone: string) => {
    const currentPhoneType = phoneRef.current?.getInstance()?.getNumberType();
    setIsPhoneMobile(
      currentPhoneType === PhoneType.MOBILE || currentPhoneType === PhoneType.TOLL_FREE
    );

    if (phone !== formData.phone) {
      handleFormDataChange({ phone });
    }
    if ((isPhoneValid && isPhoneMobile) || !phone) {
      setPhoneValidationError(null);
    }
  };

  const handleSignUpWithGoogle = (e) => {
    e.preventDefault();
    saveItem('signup', '1');

    SignupTracking.handleSignUpWithClick(SignupAuthMethod.Google)();
    submitPost(`${Config.accounts_api_url}/oauth/google/signup${window.location.search}`, {
      organization_tracking: getSignUpTracking(),
    });
  };

  const handleSignUpWithApple = (e) => {
    e.preventDefault();
    saveItem('signup', '1');

    SignupTracking.handleSignUpWithClick(SignupAuthMethod.Apple)();
    submitPost(`${Config.accounts_api_url}/oauth/apple/signup${window.location.search}`, {
      organization_tracking: getSignUpTracking(),
    });
  };

  const handleSignUpWithMicrosoft = (e) => {
    e.preventDefault();
    saveItem('signup', '1');

    SignupTracking.handleSignUpWithClick(SignupAuthMethod.Microsoft)();
    submitPost(`${Config.accounts_api_url}/oauth/microsoft/signup${window.location.search}`, {
      organization_tracking: getSignUpTracking(),
    });
  };

  const header =
    detectedProduct === Product.PartnerProgram ? 'Let’s become Partners' : 'Start for free';
  const subheader =
    detectedProduct === Product.PartnerProgram
      ? 'Grow your business with Text'
      : 'No credit card required';

  return (
    <Layout showHeaderLogo showAlreadyHaveAnAccount>
      {changedPageTitle && (
        <Helmet defer={false}>
          <title>{changedPageTitle}</title>
        </Helmet>
      )}

      <div css={styles.container} data-testid="signup-form-wrapper">
        <div css={styles.heading}>
          <Heading size="lg">{header}</Heading>
          {![Product.OpenWidget, Product.Platform].includes(detectedProduct) && (
            <Heading size="md" css={styles.subHeading}>
              {subheader}
            </Heading>
          )}
        </div>
        <div css={styles.signUpWrapper}>
          <div css={styles.formWrapper}>
            <Form onSubmit={handleSubmit}>
              {isSignupPhoneLimitException && (
                <FormMessage type={FormMessageType.Error}>
                  This phone number has been used for too many accounts. Use a different number to
                  sign up, or <CustomLink to="/">log in</CustomLink> to your existing account.
                </FormMessage>
              )}

              {shouldShowFullNameField && (
                <FormField
                  labelText="Full name"
                  labelFor="name"
                  css={styles.formField}
                  error={nameValidationError}
                >
                  <Input
                    autoFocus
                    type="text"
                    name="name"
                    placeholder="John Smith"
                    inputSize="large"
                    onChange={handleNameInputChange}
                    error={!!nameValidationError}
                  />
                </FormField>
              )}

              <FormField
                labelText="Business email"
                labelFor="email"
                css={styles.formField}
                error={emailValidationError}
              >
                <Input
                  autoFocus={!shouldShowFullNameField}
                  type="email"
                  name="email"
                  placeholder="name@work-email.com"
                  inputSize="large"
                  onChange={handleEmailInputChange}
                  value={formData.email}
                  error={!!emailValidationError}
                />
              </FormField>
              <FormField
                labelText="Password"
                labelFor="password"
                css={styles.passwordField}
                error={passwordValidationError}
              >
                <Input
                  type="password"
                  name="password"
                  placeholder="Set your password"
                  inputSize="large"
                  onChange={handlePasswordInputChange}
                  value={formData.password}
                  error={!!passwordValidationError}
                />
              </FormField>
              <PasswordRequirements
                password={formData.password}
                showErrors={!!passwordValidationError}
              />

              {shouldShowPhoneField && (
                <FormField
                  labelText="Mobile phone number"
                  labelFor="phone"
                  css={styles.phoneInput}
                  error={phoneValidationError}
                >
                  <IntlTelInput
                    ref={phoneRef}
                    initialValue={formData.phone}
                    onChangeNumber={handlePhoneInputChange}
                    onChangeValidity={setIsPhoneValid}
                    onChangeErrorCode={setPhoneErrorCode}
                    disabled={isPhoneScriptLoading}
                    inputProps={{
                      name: 'phone',
                      type: 'tel',
                      placeholder: '(123) 456-7890',
                      class:
                        phoneValidationError || (isSignupPhoneLimitException && !hasFormChanged)
                          ? 'iti__tel-input__error'
                          : 'iti__tel-input',
                      'data-testid': 'phone-input',
                    }}
                    initOptions={{
                      autoPlaceholder: 'aggressive',
                      utilsScript:
                        'https://cdn.jsdelivr.net/npm/intl-tel-input@23.8.1/build/js/utils.js',
                    }}
                  />
                </FormField>
              )}

              <Button
                type="submit"
                kind="destructive"
                size="large"
                data-testid="create-account-button"
                css={styles.signUpButton}
              >
                Sign up
              </Button>
            </Form>
          </div>

          <LoginWithButton
            onClick={handleSignUpWithGoogle}
            caption="Sign up with"
            company="Google"
          />
          <LoginWithButton
            onClick={handleSignUpWithMicrosoft}
            caption="Sign up with"
            company="Microsoft"
          />
          <LoginWithButton onClick={handleSignUpWithApple} caption="Sign up with" company="Apple" />

          <SignupLegalInfo />
        </div>
      </div>
    </Layout>
  );
};
